import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useState } from 'react'

/** SearchInput's clear button, which takes focus on pointer-down and so blurs the input first. */
const CLEAR_BUTTON = '[data-testid="clear-button"]'

type UseDisbursementSearchProps = {
  search?: string | null
  onSearch: (search?: string) => void
}

/**
 * Search applies on Enter rather than per keystroke, so the list is not refetched mid-word, and on
 * blur so a pending term cannot sit in the box contradicting the list while the user moves on to
 * another filter. The local value tracks `search` so a cleared param (e.g. back navigation) resets
 * the input.
 */
export const useDisbursementSearch = ({ search, onSearch }: UseDisbursementSearchProps) => {
  const [value, setValue] = useState(search ?? '')

  useEffect(() => {
    setValue(search ?? '')
  }, [search])

  // The local value is normalised too, not just the term sent. Submitting whitespace leaves the
  // param absent, so the resync effect never fires and the box would otherwise keep showing text —
  // and a clear button — for a search that was never applied.
  const submit = (nextValue: string) => {
    const trimmed = nextValue.trim()

    setValue(trimmed)
    onSearch(trimmed || undefined)
  }

  return {
    value,
    onChange: (event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value),
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        submit(value)
      }

      if (event.key === 'Escape') {
        setValue('')
        submit('')
      }
    },
    onBlur: (event: FocusEvent<HTMLInputElement>) => {
      // Clicking the clear button blurs the input before its own press lands. Committing here would
      // apply the very term the click is discarding: the list visibly filters, then clears again,
      // for two requests the gesture should never have made.
      if ((event.relatedTarget as HTMLElement | null)?.closest(CLEAR_BUTTON)) {
        return
      }

      submit(value)
    },
    handleClearValue: () => {
      setValue('')
      submit('')
    }
  }
}
